import { forwardRef } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, MagnifyingGlass } from "@phosphor-icons/react";
import { useMessagingStreamStatus } from "../../context/MessagingStreamContext";

// Small colored dot next to the "الرسائل" title indicating the live SSE
// stream status. Green = connected (new messages arrive live), amber =
// reconnecting after a network hiccup, hidden otherwise. Reassures
// lawyers on flaky courthouse Wi-Fi that they're not "silently offline".
function LiveDot() {
  const status = useMessagingStreamStatus();
  if (status === "open") {
    return (
      <span
        className="inline-block w-1.5 h-1.5 rounded-full bg-[#22C55E] align-middle mr-1.5"
        style={{ boxShadow: "0 0 0 3px rgba(34,197,94,0.18)" }}
        aria-label="مُتَّصِل"
        title="مُتَّصِل"
        data-testid="messages-stream-status"
        data-status="open"
      />
    );
  }
  if (status === "connecting" || status === "reconnecting") {
    return (
      <span
        className="inline-block w-1.5 h-1.5 rounded-full bg-[#F59E0B] animate-pulse align-middle mr-1.5"
        aria-label="جاري الاِتِّصال"
        title={status === "reconnecting" ? "إعادَة اِتِّصال…" : "اِتِّصال…"}
        data-testid="messages-stream-status"
        data-status={status}
      />
    );
  }
  return null;
}

// Fixed glass header portal'd to <body> to escape the page's stacking
// context. Includes the back button, dynamic subtitle + optional search
// bar (rendered only when the chat list is non-trivial).
const TopHeader = forwardRef(function TopHeader(
  { tab, totalUnread, showSearch, q, setQ, onBack },
  ref,
) {
  const subtitle = tab === "calls"
    ? <p className="text-[11px] font-tajawal text-[#5A6470] mt-0.5">سِجِلّ المُكالمات الصوتية</p>
    : totalUnread > 0
      ? <p className="text-[11px] font-tajawal text-[#E0413B] mt-0.5 font-bold">{totalUnread} غير مَقروءة</p>
      : <p className="text-[11px] font-tajawal text-[#5A6470] mt-0.5">مُحادثاتك مع المُحامين والعُملاء</p>;

  return createPortal(
    <header
      ref={ref}
      className="lg:hidden fixed top-0 inset-x-0 z-30"
      style={{
        paddingTop: "calc(max(env(safe-area-inset-top, 0px), 44px) + 4px)",
        paddingBottom: "6px",
        background: "rgba(255,255,255,0.1)",
        backdropFilter: "blur(22px) saturate(180%)",
        WebkitBackdropFilter: "blur(22px) saturate(180%)",
      }}
      dir="rtl"
      data-testid="messages-fixed-header"
    >
      <div className="px-4 flex items-center justify-between">
        <button
          onClick={onBack}
          className="w-10 h-10 rounded-full bg-white shadow-[0_2px_8px_rgba(10,25,47,0.10)] flex items-center justify-center text-[#0A192F] active:scale-95 transition"
          data-testid="messages-back"
          aria-label="رجوع"
        >
          <ArrowRight size={18} weight="bold" />
        </button>
        <div className="flex-1 text-center px-3">
          <h1 className="text-[17px] font-bold text-[#0A192F] leading-tight">
            <LiveDot />
            الرسائل
          </h1>
          {subtitle}
        </div>
        <span className="w-10 h-10" />
      </div>
      {showSearch && tab === "chats" && (
        <div className="px-4 pt-2.5">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A8A]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ابحث في المُحادثات…"
              className="w-full bg-white/85 backdrop-blur-md border border-white/60 rounded-full px-10 py-2.5 text-[13.5px] font-tajawal text-[#0A192F] placeholder-[#8A8A8A] focus:outline-none focus:border-[#D4AF37] shadow-[0_2px_10px_rgba(10,25,47,0.05)]"
              data-testid="messages-search"
            />
          </div>
        </div>
      )}
    </header>,
    document.body,
  );
});

export default TopHeader;
